import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, HelpText } from '../../components';

import css from './FieldSelect.module.css';

const optionValue = (props, label) => {
  if (props.value !== undefined && props.value !== null) {
    return String(props.value);
  }
  if (typeof label === 'string' || typeof label === 'number') {
    return String(label);
  }
  return '';
};

/**
 * Flatten <option> children into { value, label, disabled } rows.
 * Native <select> menus cannot be styled; this list drives the custom dropdown.
 *
 * @param {React.ReactNode} children
 * @returns {Array<{value: string, label: React.ReactNode, disabled: boolean}>}
 */
const parseOptions = children => {
  const options = [];
  React.Children.forEach(children, child => {
    if (!React.isValidElement(child) || child.type !== 'option') {
      return;
    }
    const label = child.props.children;
    options.push({
      value: optionValue(child.props, label),
      label,
      disabled: !!child.props.disabled,
    });
  });
  return options;
};

const FieldSelectComponent = props => {
  const {
    rootClassName,
    className,
    selectClassName,
    labelClassName,
    id,
    label,
    helpText,
    input,
    meta,
    children,
    onChange,
    showLabelAsDisabled,
    disabled = false,
    portal = false,
    searchable = false,
    searchPlaceholder = '',
    searchEmptyText = '',
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;
  const hasError = touched && invalid && error;
  const options = useMemo(() => parseOptions(children), [children]);
  const currentValue = input.value == null ? '' : String(input.value);
  const selected = options.find(o => o.value === currentValue);
  const selectedLabel = selected?.label ?? currentValue;
  const isPlaceholder = !selected || selected.value === '';

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [portalBox, setPortalBox] = useState(null);
  const [query, setQuery] = useState('');
  const queryRef = useRef('');
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const listId = id ? `${id}.list` : undefined;

  const enabledIndexes = useMemo(
    () => options.map((o, i) => (o.disabled ? -1 : i)).filter(i => i >= 0),
    [options]
  );

  const visibleOptions = useMemo(() => {
    const withIndex = options.map((option, index) => ({ ...option, index }));
    if (!searchable) {
      return withIndex;
    }
    const q = query.trim().toLowerCase();
    return withIndex.filter(option => {
      if (option.disabled && option.value === '') {
        return false;
      }
      if (!q) {
        return true;
      }
      return String(option.label ?? '')
        .toLowerCase()
        .includes(q);
    });
  }, [options, query, searchable]);

  const navigationIndexes = useMemo(
    () =>
      searchable
        ? visibleOptions.filter(option => !option.disabled).map(option => option.index)
        : enabledIndexes,
    [searchable, visibleOptions, enabledIndexes]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const selectedIndex = options.findIndex(o => o.value === currentValue && !o.disabled);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : enabledIndexes[0] ?? -1);

    const onDocumentClick = e => {
      const inTrigger = wrapperRef.current?.contains(e.target);
      const inList = listRef.current?.contains(e.target);
      if (!inTrigger && !inList) {
        setIsOpen(false);
        input.onBlur();
      }
    };
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        input.onBlur();
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
    // input.onBlur is stable enough for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentValue, options, enabledIndexes]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0 || !listRef.current) {
      return;
    }
    const el = listRef.current.querySelector(`[data-option-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, activeIndex]);

  useLayoutEffect(() => {
    if (!isOpen || !portal || !triggerRef.current) {
      setPortalBox(null);
      return undefined;
    }
    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPortalBox({
        top: rect.bottom - 1,
        left: rect.left,
        width: rect.width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, portal]);

  const commitValue = value => {
    input.onChange(value);
    if (onChange) {
      onChange(value);
    }
    setIsOpen(false);
    triggerRef.current?.focus();
    input.onBlur();
  };

  const moveActive = direction => {
    if (navigationIndexes.length === 0) {
      return;
    }
    const currentPos = navigationIndexes.indexOf(activeIndex);
    const nextPos =
      currentPos === -1
        ? direction > 0
          ? 0
          : navigationIndexes.length - 1
        : (currentPos + direction + navigationIndexes.length) % navigationIndexes.length;
    setActiveIndex(navigationIndexes[nextPos]);
  };

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      queryRef.current = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable) {
      searchRef.current?.focus();
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen || !searchable || queryRef.current === query) {
      return;
    }
    queryRef.current = query;
    setActiveIndex(navigationIndexes[0] ?? -1);
  }, [isOpen, searchable, query, navigationIndexes]);

  const handleTriggerKeyDown = e => {
    if (disabled) {
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        moveActive(e.key === 'ArrowDown' ? 1 : -1);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && activeIndex >= 0 && !options[activeIndex]?.disabled) {
        commitValue(options[activeIndex].value);
      } else {
        setIsOpen(open => !open);
      }
    }
  };

  const selectClasses = classNames(css.select, {
    [selectClassName]: selectClassName,
    [css.selectError]: hasError,
    [css.selectOpen]: isOpen,
    [css.selectPlaceholder]: isPlaceholder,
  });

  const labelClasses = classNames(css.label, {
    [css.labelDisabled]: showLabelAsDisabled,
    [labelClassName]: !!labelClassName,
  });

  const classes = classNames(rootClassName || css.root, className, {
    [css.rootOpen]: isOpen,
  });

  const handleSearchKeyDown = e => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && !options[activeIndex]?.disabled) {
        commitValue(options[activeIndex].value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
      input.onBlur();
    }
  };

  const optionItems = visibleOptions.map(option => {
    const index = option.index;
    const isSelected = option.value === currentValue;
    return (
      <li
        key={`${option.value}-${index}`}
        id={id ? `${id}.option.${index}` : undefined}
        data-option-index={index}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled || undefined}
        className={classNames(css.option, {
          [css.optionSelected]: isSelected,
          [css.optionActive]: index === activeIndex,
          [css.optionDisabled]: option.disabled,
        })}
        onMouseEnter={() => {
          if (!option.disabled) {
            setActiveIndex(index);
          }
        }}
        onMouseDown={e => {
          e.preventDefault();
        }}
        onClick={() => {
          if (!option.disabled) {
            commitValue(option.value);
          }
        }}
      >
        {option.label}
      </li>
    );
  });

  const listStyle =
    portal && portalBox
      ? {
          top: portalBox.top,
          left: portalBox.left,
          width: portalBox.width,
        }
      : undefined;

  const dropdown = isOpen ? (
    searchable ? (
      <div
        className={classNames(css.menu, { [css.dropdownPortal]: portal })}
        ref={listRef}
        style={listStyle}
      >
        <input
          ref={searchRef}
          id={id ? `${id}-search` : undefined}
          className={css.search}
          type="text"
          value={query}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder || undefined}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <ul
          className={css.dropdownList}
          id={listId}
          role="listbox"
          aria-labelledby={id}
          aria-activedescendant={activeIndex >= 0 && id ? `${id}.option.${activeIndex}` : undefined}
        >
          {navigationIndexes.length === 0 ? (
            <li className={css.empty}>{searchEmptyText}</li>
          ) : (
            optionItems
          )}
        </ul>
      </div>
    ) : (
      <ul
        className={classNames(css.dropdown, { [css.dropdownPortal]: portal })}
        id={listId}
        role="listbox"
        ref={listRef}
        aria-labelledby={id}
        aria-activedescendant={activeIndex >= 0 && id ? `${id}.option.${activeIndex}` : undefined}
        style={listStyle}
      >
        {optionItems}
      </ul>
    )
  ) : null;

  return (
    <div className={classes} ref={wrapperRef}>
      {label ? (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      ) : null}
      <div className={css.control}>
        <button
          {...rest}
          type="button"
          id={id}
          name={input.name}
          ref={triggerRef}
          className={selectClasses}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          onClick={() => {
            if (disabled) {
              return;
            }
            setIsOpen(open => !open);
          }}
          onFocus={input.onFocus}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className={css.summary}>{selectedLabel}</span>
        </button>
        {portal
          ? typeof document !== 'undefined' && dropdown
            ? createPortal(dropdown, document.body)
            : null
          : dropdown}
      </div>
      <HelpText helpText={helpText} />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field wrapping a themed single-select dropdown.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.selectClassName add more style rules to the trigger
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required so the label can reference the input in the `for` attribute
 * @param {ReactNode} props.label
 * @param {ReactNode} props.children `<option>` elements
 * @param {boolean} props.disabled Whether the select element is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @param {boolean} props.searchable Show a text field that filters the open list
 * @param {string} props.searchPlaceholder Placeholder for the filter field
 * @param {ReactNode} props.searchEmptyText Shown when the filter matches nothing
 * @returns {JSX.Element} Final Form Field containing a custom select
 */
const FieldSelect = props => {
  return <Field component={FieldSelectComponent} {...props} />;
};

export default FieldSelect;
